import type { WebCompany } from '@infra/api/BusinessApiClient';

/**
 * Cabinet en navigateur : une session ne porte qu'UNE entreprise. Quand l'e-mail et le mot de passe ouvrent des
 * comptes dans plusieurs entreprises, le serveur renvoie la liste (nom + identifiant, rien de secret) ; elle est
 * gardée pour l'onglet seulement (sessionStorage) afin d'alimenter le sélecteur d'entreprise et de préremplir
 * l'écran de connexion après une bascule. Aucun mot de passe, aucun jeton.
 */
const COMPANIES_KEY = 'nexxus.web.companies';
const PENDING_KEY = 'nexxus.web.company_pending';

export interface WebCompanyList {
  email: string;
  companies: WebCompany[];
}

export interface PendingCompanySwitch {
  email: string;
  tenant_id: string;
  companies: WebCompany[];
}

function isCompany(value: unknown): value is WebCompany {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['tenant_id'] === 'string' && typeof v['name'] === 'string';
}

function read<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* stockage indisponible : le sélecteur reste simplement masqué */
  }
}

export function rememberWebCompanies(email: string, companies: readonly WebCompany[]): void {
  write(COMPANIES_KEY, { email, companies: companies.filter(isCompany) } satisfies WebCompanyList);
}

export function readWebCompanies(): WebCompanyList | null {
  const list = read<WebCompanyList>(COMPANIES_KEY);
  if (!list || typeof list.email !== 'string' || !Array.isArray(list.companies)) return null;
  const companies = list.companies.filter(isCompany);
  return companies.length > 1 ? { email: list.email, companies } : null;
}

export function clearWebCompanies(): void {
  try {
    sessionStorage.removeItem(COMPANIES_KEY);
  } catch {
    /* ignoré */
  }
}

/** Mémorise le choix d'une bascule : l'écran de connexion qui suit présélectionne l'entreprise et l'e-mail. */
export function setPendingCompanySwitch(pending: PendingCompanySwitch): void {
  write(PENDING_KEY, pending);
}

/** Lit puis efface la bascule en attente (une seule fois). */
export function consumePendingCompanySwitch(): PendingCompanySwitch | null {
  const pending = read<PendingCompanySwitch>(PENDING_KEY);
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignoré */
  }
  if (!pending || typeof pending.email !== 'string' || typeof pending.tenant_id !== 'string') return null;
  return { email: pending.email, tenant_id: pending.tenant_id, companies: (pending.companies ?? []).filter(isCompany) };
}
